// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/sections/Testimonials.js

import React, { useState } from 'react';
import { FiStar, FiPlay, FiChevronLeft, FiChevronRight, FiMapPin } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

// Inject keyframes dynamically into the document for the smooth marquee effect
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes marqueeScroll {
      0% { transform: translateX(0); }
      100% { transform: translateX(calc(-50% - 12px)); }
    }
    .animate-marquee-loop {
      display: flex;
      width: max-content;
      animation: marqueeScroll 35s linear infinite;
    }
    .animate-marquee-loop:hover {
      animation-play-state: paused;
    }
  `;
  document.head.appendChild(style);
}

const getImage = (item, apiBaseURL) => {
  const candidate = item.studentImage || item.image || item.imageUrl || item.avatar || '';
  return buildResolvedFile(candidate, apiBaseURL).resolvedSrc || '';
};

const StarRating = ({ rating }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <FiStar
          key={i}
          className={`w-3.5 h-3.5 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
        />
      ))}
    </div>
  );
};

const TestimonialCard = ({ item, isFeatured, apiBaseURL, customWidthClass = '' }) => {
  const profileSrc = getImage(item, apiBaseURL);
  const { primary, secondary, primaryLight, secondaryLight, fontBody, fontHeading } = useBrandColors();

  return (
    <div 
      className={`bg-white rounded-3xl p-6 md:p-8 border border-gray-100 transition-all duration-500 hover:shadow-[0_20px_50px_rgba(0,0,0,0.04)] hover:-translate-y-1 flex flex-col justify-between h-full ${customWidthClass} ${
        isFeatured ? 'md:col-span-2 md:row-span-2 lg:p-10' : ''
      }`}
      style={{ fontFamily: fontBody }}
    >
      <div>
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 relative">
              <div 
                className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-md"
                style={{ background: `linear-gradient(135deg, ${primaryLight || '#F3F4F6'}, ${secondaryLight || '#E5E7EB'})` }}
              >
                {profileSrc ? (
                  <img
                    src={profileSrc}
                    alt={item.studentName}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: primary }}>
                    {item.studentName?.charAt(0) || 'S'}
                  </div>
                )}
              </div>
              {item.videoLink && (
                <button 
                  className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-white shadow-md hover:scale-110 transition-transform"
                  style={{ backgroundColor: primary }}
                >
                  <FiPlay className="w-2.5 h-2.5 ml-0.5 fill-white" />
                </button>
              )}
            </div>
            
            <div className="min-w-0">
              <h4 
                className={`font-bold text-gray-900 tracking-tight ${isFeatured ? 'text-lg md:text-xl' : 'text-base'}`}
                style={{ fontFamily: fontHeading }}
              >
                {item.studentName}
              </h4>
              <p className="text-xs text-gray-400 mt-0.5 font-medium uppercase tracking-wider">
                {item.program} {item.batch && `• ${item.batch}`}
              </p>
            </div>
          </div>

          {item.rating > 0 && <StarRating rating={item.rating} />}
        </div>

        <p className={`text-gray-600 leading-relaxed font-normal ${isFeatured ? 'text-base md:text-lg mb-6' : 'text-sm mb-4'}`}>
          "{item.testimonial}"
        </p>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between gap-4 flex-wrap">
        {item.company ? (
          <p className="text-xs font-semibold text-gray-500 tracking-wide uppercase">
            {item.currentPosition} <span style={{ color: secondary || primary }}>@ {item.company}</span>
          </p>
        ) : <div />}
        
        {item.location && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium">
            <FiMapPin className="w-3 h-3" /> {item.location}
          </span>
        )}
      </div>
    </div>
  );
};

const Testimonials = ({ testimonials, apiBaseURL = '' }) => {
  const { sectionTitle, sectionSubtitle, displayStyle = 'featured-grid', items = [] } = testimonials || {};
  const [currentPage, setCurrentPage] = useState(0);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!items || items.length === 0) return null;

  // Determine configuration settings based on requested style
  const isCarousel = displayStyle === 'carousel';
  const isMarquee = displayStyle === 'marquee' && items.length >= 2;
  const itemsPerPage = 3;
  const totalPages = Math.ceil(items.length / itemsPerPage);

  const getVisibleItems = () => {
    if (!isCarousel) return items;
    const start = currentPage * itemsPerPage;
    return items.slice(start, start + itemsPerPage);
  };

  const visibleItems = getVisibleItems();

  return (
    <section id="testimonials" className="py-20 md:py-28 bg-gray-50/40 overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        
        {/* Header Block */}
        {(sectionTitle || sectionSubtitle) && (
          <div className="max-w-3xl mb-16 md:mb-20">
            <span 
              className="inline-block text-xs font-bold tracking-[0.25em] uppercase mb-4"
              style={{ color: secondary || primary, fontFamily: fontBody }}
            >
              Success Stories
            </span>
            {sectionTitle && (
              <h2 
                className="text-3xl md:text-5xl font-black tracking-tight mb-5 text-gray-900"
                style={{ fontFamily: fontHeading }}
              >
                {sectionTitle}
              </h2>
            )}
            {sectionSubtitle && (
              <p className="text-base md:text-lg text-gray-500 font-normal leading-relaxed max-w-2xl" style={{ fontFamily: fontBody }}>
                {sectionSubtitle}
              </p>
            )}
          </div>
        )}

        {/* Dynamic Display Layout Modes */}
        {isMarquee ? (
          /* Smooth Infinite Marquee Effect Arrangement */
          <div className="relative w-full overflow-hidden mask-gradient-edges py-4">
            <div className="animate-marquee-loop gap-6">
              {/* Double array layout to fulfill seamless track cycle loop without visually snapping */}
              {[...items, ...items].map((item, index) => (
                <TestimonialCard 
                  key={`marquee-${index}`} 
                  item={item} 
                  isFeatured={false}
                  apiBaseURL={apiBaseURL}
                  customWidthClass="w-[350px] md:w-[420px] flex-shrink-0"
                />
              ))}
            </div>
          </div>
        ) : (
          /* Standard Flex / Grid / Pagination Structures */
          <>
            <div className={`grid gap-6 lg:gap-8 ${
              displayStyle === 'featured-grid' 
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-flow-row-dense' 
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {visibleItems.map((item, index) => (
                <TestimonialCard 
                  key={item._id || item.id || item.studentName || index} 
                  item={item} 
                  isFeatured={index === 0 && displayStyle === 'featured-grid'}
                  apiBaseURL={apiBaseURL}
                />
              ))}
            </div>

            {isCarousel && totalPages > 1 && (
              <div className="flex items-center justify-end gap-3 mt-10">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                  className="p-3 rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-gray-700"
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm font-medium text-gray-400 px-2" style={{ fontFamily: fontBody }}>
                  {currentPage + 1} <span className="text-gray-300 mx-1">/</span> {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage === totalPages - 1}
                  className="p-3 rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-gray-700"
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Testimonials;